import { c as _c } from "react/compiler-runtime";
import { Calendar, MapPin, PencilSquare, User } from '@byway/icons';
import { useTranslations } from 'next-intl';
import { shortDateMonth } from 'utils';
import useJourneyData from '~/hooks/useJourneyData';
import { useFormRef, useMobileEditPanel } from '~/stores/journeyStore';
import { trackEvent } from '~/utils';
import { isJourneyExpired } from '~/utils/dateHelpers/dateHelpers';
import { Button } from '../Button';
import { Divider } from '../Divider';
import { Box } from '../Layout/Box';
import { Stack } from '../Layout/Stack';
import styles from './MobileEditBlock.module.css';
export function MobileEditBlock() {
  const $ = _c(105);
  const {
    setIsMobileEditPanelOpen,
    setMobileEditPanelIndex
  } = useMobileEditPanel();
  const {
    details,
    futurePricingTiers,
    source,
    expirationDateTime,
    bookingInformation,
    overrideEditableTrip,
    journeyType
  } = useJourneyData();
  const editForm = useFormRef();
  const t = useTranslations("journey");
  const tAria = useTranslations("aria");
  const tCommon = useTranslations("common");
  const isBlueprintJourney = source.creationSource === "FROM_TEMPLATE" || source.creationSource === "FROM_VIRTUAL_BLUEPRINT_TEMPLATE" || source.creationSource === "FROM_VIRTUAL_BLUEPRINT";
  const isBlueprint = isBlueprintJourney;
  const isTemplatePage = journeyType === "asTemplate";
  const isEarliestCheapestDate = details.startDate === futurePricingTiers?.cheapestAvailableDate;
  const isDefaultOrigin = isEarliestCheapestDate && editForm?.values?.closest_city === "London";
  const isDefaultPax = isEarliestCheapestDate && editForm?.values?.adult_count === 2 && editForm?.values?.room_count === 1 && editForm?.values?.child_count === 0;
  const t0 = editForm?.values?.adult_count ?? 0;
  let t1;
  if ($[0] !== t || $[1] !== t0) {
    t1 = t("pax_adults_title", {
      count: t0
    });
    $[0] = t;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const adultString = t1;
  let t2;
  if ($[3] !== editForm || $[4] !== t) {
    t2 = editForm?.values?.child_count ? t("pax_children", {
      count: editForm.values.child_count
    }) : "";
    $[3] = editForm;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const childString = t2;
  const childSuffix = childString ? `, ${childString}` : "";
  const paxString = `${adultString}${childSuffix}`;
  const isEnquireOnly = process.env.NEXT_PUBLIC_FORCE_ENQUIRY === "true";
  const isBookedJourney = bookingInformation?.bookingStatus === "BOOKED" || bookingInformation?.bookingStatus === "OUTSTANDING";
  let t3;
  if ($[6] !== expirationDateTime || $[7] !== isBlueprint) {
    t3 = isJourneyExpired(expirationDateTime, isBlueprint);
    $[6] = expirationDateTime;
    $[7] = isBlueprint;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const isExpired = t3;
  let isEditable;
  if ($[9] !== isBookedJourney || $[10] !== isExpired || $[11] !== overrideEditableTrip) {
    isEditable = !isEnquireOnly && !isBookedJourney && !isExpired;
    if (typeof overrideEditableTrip === "boolean") {
      isEditable = overrideEditableTrip;
    }
    $[9] = isBookedJourney;
    $[10] = isExpired;
    $[11] = overrideEditableTrip;
    $[12] = isEditable;
  } else {
    isEditable = $[12];
  }
  let t4;
  if ($[13] !== isEditable || $[14] !== setIsMobileEditPanelOpen || $[15] !== setMobileEditPanelIndex) {
    t4 = () => {
      if (!isEditable) {
        return;
      }
      trackEvent("trip-page-edit-click-dates", {
        event_category: "click",
        event_label: "trip-page-edit-click-dates"
      });
      setIsMobileEditPanelOpen(true);
      setMobileEditPanelIndex(0);
    };
    $[13] = isEditable;
    $[14] = setIsMobileEditPanelOpen;
    $[15] = setMobileEditPanelIndex;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const dateHandler = t4;
  let t5;
  if ($[17] !== isEditable || $[18] !== setIsMobileEditPanelOpen || $[19] !== setMobileEditPanelIndex) {
    t5 = () => {
      if (!isEditable) {
        return;
      }
      trackEvent("trip-page-edit-click-origin", {
        event_category: "click",
        event_label: "trip-page-edit-click-origin"
      });
      setIsMobileEditPanelOpen(true);
      setMobileEditPanelIndex(1);
    };
    $[17] = isEditable;
    $[18] = setIsMobileEditPanelOpen;
    $[19] = setMobileEditPanelIndex;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const originHandler = t5;
  let t6;
  if ($[21] !== isEditable || $[22] !== setIsMobileEditPanelOpen || $[23] !== setMobileEditPanelIndex) {
    t6 = () => {
      if (!isEditable) {
        return;
      }
      trackEvent("trip-page-edit-click-pax", {
        event_category: "click",
        event_label: "trip-page-edit-click-pax"
      });
      setIsMobileEditPanelOpen(true);
      setMobileEditPanelIndex(2);
    };
    $[21] = isEditable;
    $[22] = setIsMobileEditPanelOpen;
    $[23] = setMobileEditPanelIndex;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const travellersHandler = t6;
  let t7;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Divider variant="horizontal" />;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== isEditable || $[27] !== t) {
    t8 = isEditable ? t("edit_dates_choose") : t("edit_dates_your");
    $[26] = isEditable;
    $[27] = t;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== t8) {
    t9 = <h3 className={styles.title}>{t8}</h3>;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span><Calendar decorative={true} size="large" /></span>;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== details.endDate || $[33] !== details.startDate || $[34] !== details.tripInDays || $[35] !== isEarliestCheapestDate || $[36] !== isTemplatePage || $[37] !== t) {
    t11 = <div className={styles.description}>{t10}<p className={styles.text}>{isTemplatePage ? <strong>{t("days_count", {
            count: details.tripInDays
          })}</strong> : <><strong>{`${shortDateMonth(details.startDate)} - ${shortDateMonth(details.endDate)} `}</strong>{isEarliestCheapestDate && <span className="inline-block">{t("cheapest_dates_preselected")}</span>}</>}</p></div>;
    $[32] = details.endDate;
    $[33] = details.startDate;
    $[34] = details.tripInDays;
    $[35] = isEarliestCheapestDate;
    $[36] = isTemplatePage;
    $[37] = t;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== t11 || $[40] !== t9) {
    t12 = <div className={styles.textBlock}>{t9}{t11}</div>;
    $[39] = t11;
    $[40] = t9;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== dateHandler || $[43] !== isEditable || $[44] !== tAria) {
    t13 = isEditable && <Button data-ua-click="mobile-edit-block-dates" variant="icon" onPress={dateHandler} ariaLabel={tAria("change_dates")}><PencilSquare decorative={true} /></Button>;
    $[42] = dateHandler;
    $[43] = isEditable;
    $[44] = tAria;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[46] !== dateHandler || $[47] !== t12 || $[48] !== t13) {
    t14 = <div className={styles.cta} data-ua-click="mobile-edit-block-dates" onClick={dateHandler}>{t12}{t13}</div>;
    $[46] = dateHandler;
    $[47] = t12;
    $[48] = t13;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  let t15;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Divider variant="horizontal" />;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] !== t) {
    t16 = t("edit_origin_heading");
    $[51] = t;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] !== t16) {
    t17 = <h3 className={styles.title}>{t16}</h3>;
    $[53] = t16;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span><MapPin decorative={true} size="large" /></span>;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  const t19 = editForm?.values?.closest_city;
  let t20;
  if ($[56] !== t19) {
    t20 = <strong>{t19}</strong>;
    $[56] = t19;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] !== isDefaultOrigin || $[59] !== t) {
    t21 = isDefaultOrigin && <span> {t("selected_by_default")}</span>;
    $[58] = isDefaultOrigin;
    $[59] = t;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== t20 || $[62] !== t21) {
    t22 = <div className={styles.description}>{t18}<p className={styles.text}>{t20}{t21}</p></div>;
    $[61] = t20;
    $[62] = t21;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== t17 || $[65] !== t22) {
    t23 = <div className={styles.textBlock}>{t17}{t22}</div>;
    $[64] = t17;
    $[65] = t22;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] !== isEditable || $[68] !== originHandler || $[69] !== tCommon) {
    t24 = isEditable && <Button variant="icon" onPress={originHandler} ariaLabel={tCommon("change_origin")} data-ua-click="mobile-edit-block-origin"><PencilSquare decorative={true} /></Button>;
    $[67] = isEditable;
    $[68] = originHandler;
    $[69] = tCommon;
    $[70] = t24;
  } else {
    t24 = $[70];
  }
  let t25;
  if ($[71] !== originHandler || $[72] !== t23 || $[73] !== t24) {
    t25 = <div className={styles.cta} data-ua-click="mobile-edit-block-origin" onClick={originHandler}>{t23}{t24}</div>;
    $[71] = originHandler;
    $[72] = t23;
    $[73] = t24;
    $[74] = t25;
  } else {
    t25 = $[74];
  }
  let t26;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Divider variant="horizontal" />;
    $[75] = t26;
  } else {
    t26 = $[75];
  }
  let t27;
  if ($[76] !== t) {
    t27 = t("edit_travellers_heading");
    $[76] = t;
    $[77] = t27;
  } else {
    t27 = $[77];
  }
  let t28;
  if ($[78] !== t27) {
    t28 = <h3 className={styles.title}>{t27}</h3>;
    $[78] = t27;
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  let t29;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <User decorative={true} size="large" />;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  let t30;
  if ($[81] !== paxString) {
    t30 = <strong>{paxString}</strong>;
    $[81] = paxString;
    $[82] = t30;
  } else {
    t30 = $[82];
  }
  let t31;
  if ($[83] !== isDefaultPax || $[84] !== t) {
    t31 = isDefaultPax && <span> {t("selected_by_default")}</span>;
    $[83] = isDefaultPax;
    $[84] = t;
    $[85] = t31;
  } else {
    t31 = $[85];
  }
  let t32;
  if ($[86] !== t30 || $[87] !== t31) {
    t32 = <div className={styles.description}>{t29}<p className={styles.text}>{t30}{t31}</p></div>;
    $[86] = t30;
    $[87] = t31;
    $[88] = t32;
  } else {
    t32 = $[88];
  }
  let t33;
  if ($[89] !== t28 || $[90] !== t32) {
    t33 = <div className={styles.textBlock}>{t28}{t32}</div>;
    $[89] = t28;
    $[90] = t32;
    $[91] = t33;
  } else {
    t33 = $[91];
  }
  let t34;
  if ($[92] !== isEditable || $[93] !== tAria || $[94] !== travellersHandler) {
    t34 = isEditable && <Button variant="icon" onPress={travellersHandler} ariaLabel={tAria("change_travellers")} data-ua-click="mobile-edit-block-travellers"><PencilSquare decorative={true} /></Button>;
    $[92] = isEditable;
    $[93] = tAria;
    $[94] = travellersHandler;
    $[95] = t34;
  } else {
    t34 = $[95];
  }
  let t35;
  if ($[96] !== t33 || $[97] !== t34 || $[98] !== travellersHandler) {
    t35 = <div className={styles.cta} data-ua-click="mobile-edit-block-travellers" onClick={travellersHandler}>{t33}{t34}</div>;
    $[96] = t33;
    $[97] = t34;
    $[98] = travellersHandler;
    $[99] = t35;
  } else {
    t35 = $[99];
  }
  let t36;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <Divider variant="horizontal" />;
    $[100] = t36;
  } else {
    t36 = $[100];
  }
  let t37;
  if ($[101] !== t14 || $[102] !== t25 || $[103] !== t35) {
    t37 = <Box spaceVertical="--by-space-200"><Stack space="--by-space-100">{t7}{t14}{t15}{t25}{t26}{t35}{t36}</Stack></Box>;
    $[101] = t14;
    $[102] = t25;
    $[103] = t35;
    $[104] = t37;
  } else {
    t37 = $[104];
  }
  return t37;
}